import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Copy, Plus, Webhook, Activity, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const EVENTS = [
  "invoice.created", "invoice.updated", "invoice.status_changed",
  "contract.created", "contract.updated", "contract.corrected",
  "child.created", "child.updated",
  "presentie.updated",
  // Payroll-integratie events (Nanny-Payroll app)
  "staff.medewerker.created", "staff.medewerker.updated",
  "staff.hours.registered", "staff.verlof.created",
];

export default function WebhooksPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(init());
  const [deliveries, setDeliveries] = useState(null); // {webhook, list}

  const load = useCallback(async () => { const r = await api.get("/webhooks"); setRows(r.data); }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try { await api.post("/webhooks", form); toast.success("Webhook aangemaakt"); setOpen(false); setForm(init()); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!confirm("Webhook verwijderen?")) return;
    try { await api.delete(`/webhooks/${id}`); toast.success("Verwijderd"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openDeliveries = async (wh) => {
    const r = await api.get(`/webhooks/${wh.id}/deliveries`);
    setDeliveries({ webhook: wh, list: r.data });
  };

  return (
    <div>
      <PageHeader
        title="Webhooks & API-koppelingen"
        subtitle="Realtime notificaties naar externe systemen zoals boekhouding en roosterprogramma's."
        testid="webhooks-header"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button style={{ background: "var(--sage)" }} className="text-white" data-testid="add-webhook-btn">
                <Plus className="h-4 w-4 mr-1" /> Nieuwe webhook
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-lg">
              <DialogHeader>
                <DialogTitle>Nieuwe webhook</DialogTitle>
                <DialogDescription>Bepaal een endpoint-URL en events waarop Webster POST-requests stuurt. Elke levering is HMAC-SHA256 gesigned.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div><Label>Naam</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Bijv. Exact Online" /></div>
                <div><Label>Endpoint URL</Label><Input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://voorbeeld.nl/webhook" /></div>
                <div>
                  <Label>Events</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {EVENTS.map(e => {
                      const on = form.events.includes(e);
                      return (
                        <label key={e} className="flex items-center gap-2 text-sm p-2 rounded border border-neutral-200 hover:bg-neutral-50">
                          <Checkbox checked={on} onCheckedChange={v => setForm({ ...form, events: v ? [...form.events, e] : form.events.filter(x => x !== e) })} />
                          <span className="font-mono text-xs">{e}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
                <Button onClick={submit} style={{ background: "var(--sage)" }} className="text-white">Aanmaken</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rows.length === 0 && (
          <Card className="col-span-2 p-8 bg-white shadow-none text-center">
            <Webhook className="h-8 w-8 mx-auto text-neutral-300 mb-3" />
            <div className="text-sm text-neutral-600">Nog geen webhooks. Voeg er een toe om Exact/Twinfield/e-Boekhouden of een roosterprogramma te koppelen.</div>
          </Card>
        )}
        {rows.map(w => (
          <Card key={w.id} className="p-5 bg-white shadow-none">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-medium text-neutral-800 flex items-center gap-2">
                  <Webhook className="h-4 w-4 text-[color:var(--sage)]" />
                  {w.name}
                </div>
                <div className="text-xs text-neutral-500 font-mono mt-1 truncate max-w-md">{w.url}</div>
              </div>
              <Badge variant="outline" className={w.active ? "bg-emerald-50 text-emerald-700 border-emerald-200 border" : "bg-neutral-100 text-neutral-500 border-neutral-200 border"}>
                {w.active ? "Actief" : "Inactief"}
              </Badge>
            </div>
            <div className="flex flex-wrap gap-1 mb-3">
              {w.events.map(e => <Badge key={e} variant="outline" className="text-[10px] font-mono">{e}</Badge>)}
            </div>
            {w.secret && (
              <div className="flex items-center gap-2 mb-3 p-2 bg-neutral-50 rounded text-xs">
                <span className="text-neutral-500 uppercase tracking-wider text-[10px]">Secret</span>
                <span className="font-mono truncate flex-1">{w.secret.slice(0, 24)}…</span>
                <button className="text-neutral-500 hover:text-neutral-800" onClick={() => { navigator.clipboard.writeText(w.secret); toast.success("Secret gekopieerd"); }}>
                  <Copy className="h-3 w-3" />
                </button>
              </div>
            )}
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                {w.last_status >= 200 && w.last_status < 300
                  ? <><CheckCircle2 className="h-3 w-3 text-emerald-600" /><span className="text-neutral-600">Laatste: {w.last_status}</span></>
                  : w.last_status
                    ? <><XCircle className="h-3 w-3 text-red-600" /><span className="text-neutral-600">Laatste: {w.last_status || "fout"}</span></>
                    : <span className="text-neutral-400">Nog niet verzonden</span>}
                {w.fail_count > 0 && <span className="text-red-600">{w.fail_count} mislukt</span>}
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => openDeliveries(w)}><Activity className="h-3 w-3 mr-1" /> Logs</Button>
                <Button variant="ghost" size="sm" className="text-red-600" onClick={() => del(w.id)}>Verwijderen</Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {deliveries && (
        <Dialog open={!!deliveries} onOpenChange={() => setDeliveries(null)}>
          <DialogContent className="bg-white max-w-3xl">
            <DialogHeader>
              <DialogTitle>Deliveries — {deliveries.webhook.name}</DialogTitle>
              <DialogDescription>Laatste {deliveries.list.length} pogingen.</DialogDescription>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto space-y-2">
              {deliveries.list.length === 0 && <div className="text-sm text-neutral-500 p-4">Nog geen deliveries.</div>}
              {deliveries.list.map(d => (
                <div key={d.id} className="border border-neutral-200 rounded p-3 text-xs">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={d.status_code && d.status_code >= 200 && d.status_code < 300 ? "bg-emerald-50 text-emerald-700 border-emerald-200 border" : "bg-red-50 text-red-700 border-red-200 border"}>
                        {d.status_code || "ERR"}
                      </Badge>
                      <span className="font-mono">{d.event}</span>
                    </div>
                    <span className="text-neutral-500">{new Date(d.created_at).toLocaleString('nl-NL')}</span>
                  </div>
                  {d.error && <div className="text-red-600 mt-1">{d.error}</div>}
                  {d.response_body && <div className="text-neutral-500 font-mono mt-1 line-clamp-2">{d.response_body}</div>}
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function init() {
  return { name: "", url: "", secret: "", events: [], active: true };
}
